import { valibotResolver } from "@hookform/resolvers/valibot";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { AppInput } from "~/components/AppInput";
import { Button } from "~/components/ui/button";
import { ForgotPasswordForm, forgotPasswordSchema } from "~/lib/schemas";
import { forgotPasswordServer } from "~/lib/services/user";

export const Route = createFileRoute("/forgot-password")({
  component: ForgotPassword,
});

function ForgotPassword() {
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const methods = useForm<ForgotPasswordForm>({
    resolver: valibotResolver(forgotPasswordSchema),
  });

  const onSubmit = async (data: ForgotPasswordForm) => {
    setError(null);
    setIsLoading(true);
    try {
      const res = await forgotPasswordServer({ data });
      if (res?.error) {
        setError(res.error);
        return;
      }
      setSubmitted(true);
    } catch {
      setError("Could not send reset email. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-dvh flex flex-col max-w-2xl mx-auto p-6">
      <div className="mt-12 mb-8">
        <h1 className="text-4xl font-bold text-black">Forgot password</h1>
        <p className="text-lg mt-2 text-foreground-muted">
          {submitted
            ? "We've sent you a link to reset your password."
            : "Enter your email and we'll send you a link to reset your password."}
        </p>
      </div>

      {submitted ? (
        <div className="space-y-4">
          <p className="text-sm text-foreground-muted">
            The link will expire in 1 hour. Check your spam folder if you don't see
            it within a couple of minutes.
          </p>
          <p className="text-sm text-foreground-muted">
            You can close this window.
          </p>
        </div>
      ) : (
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)} className="space-y-6">
            <AppInput name="email" type="email" label="Email" />
            <Button isLoading={isLoading} type="submit" className="w-full">
              Send reset link
            </Button>
            <Link
              to="/login"
              className="block text-center text-sm underline font-semibold mt-2"
            >
              Back to log in
            </Link>
          </form>
        </FormProvider>
      )}

      {error && (
        <p className="text-destructive text-sm text-center py-4">{error}</p>
      )}
    </div>
  );
}
